/**
 * Public Navbar Component
 * 
 * Navigation bar for all public pages (Landing, Pricing, Subscribe, Legal, Auth).
 * Mobile-first responsive design with hamburger menu.
 */

import React, { useState, useEffect } from "react";
import { login } from "../../services/authApi";
import ZakyamLogo from "../layout/ZakyamLogo";
import "./public-navbar.css";

interface PublicNavbarProps {
  currentPath?: string;
}

export function PublicNavbar({ currentPath }: PublicNavbarProps): JSX.Element {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Close mobile menu on route change
    setIsMobileMenuOpen(false);
  }, [currentPath]);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const isActive = (path: string): boolean => {
    if (!currentPath) return false;
    return currentPath === path || currentPath.startsWith(path + "/");
  };

  const handleLogin = () => {
    // Default to "/" (home page) instead of "/app" which doesn't exist
    const returnTo = currentPath || "/";
    login(returnTo);
  };

  const handleRegister = () => {
    // Redirect to register page which will handle Auth0 signup
    // After registration, redirect to pricing page so user can select a plan
    const returnTo = currentPath === "/" ? "/pricing" : (currentPath || "/pricing");
    window.location.href = `/auth/register?returnTo=${encodeURIComponent(returnTo)}`;
  };

  return (
    <nav className="public-navbar" role="navigation" aria-label="Main navigation">
      <div className="public-navbar__container">
        {/* Logo and Brand */}
        <div className="public-navbar__brand">
          <a href="/" className="public-navbar__logo-link" aria-label="Zakyam AI Trader Home">
            <ZakyamLogo className="public-navbar__logo" />
          </a>
        </div>

        {/* Desktop Navigation */}
        <div className="public-navbar__desktop-nav">
          <a
            href="/pricing"
            className={`public-navbar__link ${isActive("/pricing") ? "public-navbar__link--active" : ""}`}
            aria-current={isActive("/pricing") ? "page" : undefined}
          >
            Pricing
          </a>
          <a
            href="/support"
            className={`public-navbar__link ${isActive("/support") ? "public-navbar__link--active" : ""}`}
            aria-current={isActive("/support") ? "page" : undefined}
          >
            Support
          </a>
        </div>

        {/* Desktop CTAs */}
        {/* Hide Login/Start buttons on auth pages (LoginBridgePage and RegisterPage have their own buttons) */}
        {currentPath !== "/auth/login" && currentPath !== "/auth/register" && (
          <div className="public-navbar__desktop-ctas">
            <button
              type="button"
              className="public-navbar__cta public-navbar__cta--secondary"
              onClick={handleLogin}
              aria-label="Log in to your account"
            >
              Login
            </button>
            <button
              type="button"
              className="public-navbar__cta public-navbar__cta--primary"
              onClick={handleRegister}
              aria-label="Create a new account"
            >
              Start
            </button>
          </div>
        )}

        {/* Mobile Menu Toggle */}
        <button
          type="button"
          className="public-navbar__mobile-toggle"
          onClick={handleMobileMenuToggle}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-menu"
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
        >
          <span className="public-navbar__mobile-toggle-icon" aria-hidden="true">
            {isMobileMenuOpen ? "✕" : "☰"}
          </span>
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          id="mobile-menu"
          className="public-navbar__mobile-menu"
          role="menu"
        >
          <a
            href="/pricing"
            className={`public-navbar__mobile-link ${isActive("/pricing") ? "public-navbar__mobile-link--active" : ""}`}
            onClick={handleMobileMenuClose}
            role="menuitem"
            aria-current={isActive("/pricing") ? "page" : undefined}
          >
            Pricing
          </a>
          <a
            href="/support"
            className={`public-navbar__mobile-link ${isActive("/support") ? "public-navbar__mobile-link--active" : ""}`}
            onClick={handleMobileMenuClose}
            role="menuitem"
            aria-current={isActive("/support") ? "page" : undefined}
          >
            Support
          </a>
          {/* Hide Login/Start buttons on auth pages (LoginBridgePage and RegisterPage have their own buttons) */}
          {currentPath !== "/auth/login" && currentPath !== "/auth/register" && (
            <div className="public-navbar__mobile-ctas">
              <button
                type="button"
                className="public-navbar__cta public-navbar__cta--secondary"
                onClick={() => {
                  handleMobileMenuClose();
                  handleLogin();
                }}
                role="menuitem"
              >
                Login
              </button>
              <button
                type="button"
                className="public-navbar__cta public-navbar__cta--primary"
                onClick={() => {
                  handleMobileMenuClose();
                  handleRegister();
                }}
                role="menuitem"
              >
                Start
              </button>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}

export default PublicNavbar;

